import { useEffect, useState } from "react"
import { Box, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { If } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { RateCardSkillOverrides } from "./rate-card-skill-overrides"
import { RateCardTradePricing } from "./rate-card-trade-pricing"

const MODE_HOURLY = "hourly"
const MODE_FIXED = "fixed-annual"

export function RateCardTradeRow({ item, currency, skillMapId, onUpdateLine, readOnly }) {
    const [notes, setNotes] = useState(item.notes || "")
    // Notes panel starts collapsed even when notes already exist. The filled icon signals their presence.
    const [notesOpen, setNotesOpen] = useState(false)
    const [skillsOpen, setSkillsOpen] = useState(false)
    const mode = item.mode === MODE_FIXED ? MODE_FIXED : MODE_HOURLY
    const overrideCount = (item.skillOverrides || []).length
    const skillsTooltipTitle = getSkillsTooltipTitle()
    const notesTooltipTitle = getNotesTooltipTitle()
    const canUseNotes = !readOnly || Boolean(notes)
    const notesIcon = getNotesIcon()
    const notesColor = getNotesColor()
    const skillsIcon = getSkillsIcon()
    const skillsColor = getSkillsColor()
    useEffect(syncNotesFromItem, [item.notes])

    return (
        <Box
            data-cy="rate-card-trade-row"
            sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                ...(item.isNew && { bgcolor: "success.lighter" }),
                ...(item.isOrphaned && { bgcolor: "warning.lighter", opacity: 0.7 }),
            }}
        >
            <ListItemBox sx={{ px: 2, py: 1.5, gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontSize: 16, ...(item.isOrphaned && { textDecoration: "line-through" }) }}
                    >
                        {item.tradeName || item.tradeId}
                    </Typography>
                    <If value={item.isNew}>
                        <Chip
                            data-cy="trade-new-indicator"
                            label="New"
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    </If>
                    <If value={item.isOrphaned}>
                        <Chip
                            data-cy="trade-orphaned-indicator"
                            label="Removed from skill map"
                            size="small"
                            color="warning"
                            variant="outlined"
                        />
                    </If>
                    <If value={mode === MODE_FIXED}>
                        <Chip
                            data-cy="trade-fixed-indicator"
                            label="Fixed annual"
                            size="small"
                            color="info"
                            variant="outlined"
                        />
                    </If>
                </Box>
                <RateCardTradePricing item={item} currency={currency} onUpdateLine={onUpdateLine} readOnly={readOnly} />
                {/* Reserve the skill-overrides slot in both modes so the notes icon stays put when toggling */}
                <If value={!!skillMapId}>
                    <Box sx={{ width: 30, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                        <If value={mode === MODE_HOURLY}>
                            <Tooltip title={skillsTooltipTitle} placement="top">
                                <IconButton
                                    data-cy="trade-skill-overrides-toggle"
                                    size="small"
                                    onClick={toggleSkills}
                                    color={skillsColor}
                                >
                                    <Iconify icon={skillsIcon} width={20} />
                                </IconButton>
                            </Tooltip>
                        </If>
                    </Box>
                </If>
                <Tooltip title={notesTooltipTitle} placement="top">
                    <IconButton
                        data-cy="trade-notes-toggle"
                        size="small"
                        onClick={toggleNotes}
                        disabled={!canUseNotes}
                        color={notesColor}
                    >
                        <Iconify icon={notesIcon} width={20} />
                    </IconButton>
                </Tooltip>
            </ListItemBox>
            <If value={skillsOpen && mode === MODE_HOURLY && !!skillMapId}>
                <RateCardSkillOverrides
                    line={item}
                    skillMapId={skillMapId}
                    currency={currency}
                    onChange={handleSkillOverridesChange}
                    readOnly={readOnly}
                />
            </If>
            <If value={notesOpen}>
                <Box sx={{ px: 2, pb: 1.5 }}>
                    <TextField
                        size="small"
                        data-cy="trade-notes"
                        value={notes}
                        onChange={handleNotesChange}
                        onBlur={handleNotesBlur}
                        disabled={readOnly}
                        placeholder="Optional note..."
                        fullWidth
                        multiline
                        minRows={2}
                        autoFocus={notesOpen && !notes}
                    />
                </Box>
            </If>
        </Box>
    )

    function syncNotesFromItem() {
        setNotes(item.notes || "")
    }

    function toggleNotes() {
        if (!canUseNotes) return
        setNotesOpen((prev) => !prev)
    }

    function toggleSkills() {
        setSkillsOpen((prev) => !prev)
    }

    function handleSkillOverridesChange(next) {
        if (readOnly) return
        onUpdateLine(item.tradeId, "skillOverrides", next)
    }

    function handleNotesChange(e) {
        if (readOnly) return
        setNotes(e.target.value)
    }

    function handleNotesBlur() {
        if (readOnly) return
        onUpdateLine(item.tradeId, "notes", notes)
    }

    function getSkillsTooltipTitle() {
        if (skillsOpen) return "Hide per-skill rates"
        if (overrideCount) return `${overrideCount} override${overrideCount === 1 ? "" : "s"}`
        return "Set per-skill rates"
    }

    function getNotesTooltipTitle() {
        if (notesOpen) return "Hide note"
        if (notes) return "View note"
        if (readOnly) return "No note"
        return "Add note"
    }

    function getNotesIcon() {
        if (notes) return "solar:notes-bold"
        return "solar:notes-outline"
    }

    function getNotesColor() {
        if (notes) return "primary"
        return "default"
    }

    function getSkillsIcon() {
        if (overrideCount) return "mdi:tune-variant"
        return "mdi:tune"
    }

    function getSkillsColor() {
        if (overrideCount) return "primary"
        return "default"
    }
}
